import type { ServerSummary } from '@comficonf/shared';
import { BRIDGE_WS_PATH } from '@comficonf/shared';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useId } from 'react';
import { Link } from 'react-router';
import {
  columnHeadClasses,
  eyebrowClasses,
  PageContainer,
  PageHeader,
  PageSection,
  panelClasses,
  stretchedLinkClasses,
} from '@/components/page';
import { StatusLabel } from '@/components/status-dot';
import { Button } from '@/components/ui/button';
import { LoadingStatus, Skeleton } from '@/components/ui/skeleton';
import { useLoadError } from '@/components/use-load-error';
import { serversQuery, useSession } from '@/lib/api/queries';
import { formatRelativeTime } from '@/lib/format';
import { useDocumentTitle, useNow } from '@/lib/hooks';
import { cn, pluralize } from '@/lib/utils';
import { LoadFailedNotice, RefreshFailedNotice } from './refresh-notice';

/** The bridge's `backend-url` when its `config.yml` sets none (`BridgeSettings.DEFAULT_BACKEND_URL`). */
export const HOSTED_BRIDGE_URL = 'wss://app.comficonf.com/api/v1/bridge';

/** WebSocket URL the bridge's `backend-url` must point to, derived from where the API is served. */
export function bridgeUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL || window.location.origin;
  const url = new URL(BRIDGE_WS_PATH, base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

export function lastSeenText(
  server: Pick<ServerSummary, 'status' | 'lastSeenAt'>,
  now: number,
): string {
  if (server.status === 'online') return 'Connected now';
  if (!server.lastSeenAt) return 'Never connected';
  return `Last seen ${formatRelativeTime(server.lastSeenAt, now)}`;
}

/**
 * Column template of the server table, applied from a 42rem wide panel. Every row is its own grid, so each track has
 * a fixed or fractional size. The software track is at least as wide as one line of a Paper build version.
 */
const COLUMNS =
  '@2xl/servers:grid-cols-[minmax(0,1.4fr)_6rem_minmax(11rem,1fr)_4.5rem_4.5rem_7.5rem]';

function platformText(server: ServerSummary): string | null {
  if (server.platform && server.platformVersion) {
    return `${server.platform} ${server.platformVersion}`;
  }
  return server.platform;
}

/** A labelled value of a server row. The label shows while the row is stacked and is visually hidden in the table. */
function Cell({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: string | undefined;
}) {
  const known = value !== null && value !== undefined;
  return (
    <div className="min-w-0">
      <span className={cn(eyebrowClasses, 'block @2xl/servers:sr-only')}>{label}</span>
      <span
        className={cn('block break-words font-mono text-xs', known ? 'text-text' : 'text-subtle')}
      >
        {known ? value : 'Unknown'}
      </span>
      {sub ? <span className="block break-words text-xs text-muted">{sub}</span> : null}
    </div>
  );
}

function ServerRow({ server, now }: { server: ServerSummary; now: number }) {
  const statusId = useId();
  const lastSeenId = useId();
  const online = server.status === 'online';
  return (
    <li
      className={cn(
        'relative grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 px-4 py-2.5 transition-colors hover:bg-hover @2xl/servers:items-center',
        COLUMNS,
      )}
    >
      <div className="min-w-0">
        <Link
          to={`/servers/${server.id}`}
          aria-describedby={online ? statusId : `${statusId} ${lastSeenId}`}
          className={cn('break-words text-sm font-medium text-text', stretchedLinkClasses)}
        >
          {server.displayName}
        </Link>
        {online ? null : (
          <span id={lastSeenId} className="block text-xs text-subtle">
            {lastSeenText(server, now)}
          </span>
        )}
      </div>
      <div id={statusId} className="text-right @2xl/servers:text-left">
        <StatusLabel status={server.status} />
      </div>
      <div className="col-span-2 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4 @2xl/servers:contents">
        <Cell
          label="Software"
          value={platformText(server)}
          sub={server.minecraftVersion ? `Minecraft ${server.minecraftVersion}` : undefined}
        />
        <Cell label="Bridge" value={server.bridgeVersion} />
        <Cell label="Plugins" value={server.pluginCount} />
        <Cell label="Settings pages" value={server.resourceCount} />
      </div>
    </li>
  );
}

function ServerTable({ list, now }: { list: ServerSummary[]; now: number }) {
  return (
    <div className={cn(panelClasses, '@container/servers')}>
      <div
        aria-hidden
        className={cn(columnHeadClasses, 'hidden gap-x-3 @2xl/servers:grid', COLUMNS)}
      >
        <span>Server</span>
        <span>Status</span>
        <span>Software</span>
        <span>Bridge</span>
        <span>Plugins</span>
        <span>Settings pages</span>
      </div>
      <ul aria-label="Servers" className="divide-y divide-border-soft">
        {list.map((server) => (
          <ServerRow key={server.id} server={server} now={now} />
        ))}
      </ul>
    </div>
  );
}

function ServerSkeleton() {
  return (
    <LoadingStatus
      label="Loading servers"
      className={cn(panelClasses, 'divide-y divide-border-soft')}
    >
      {[0, 1, 2].map((row) => (
        <div key={row} className="px-4 py-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-1.5 h-3 w-64 max-w-full" />
        </div>
      ))}
    </LoadingStatus>
  );
}

const codeChip = 'rounded-xs bg-sunken px-1 py-px font-mono text-xs [overflow-wrap:anywhere]';

export function OnboardingSteps({ compact = false }: { compact?: boolean }) {
  const bridge = bridgeUrl();
  const steps = [
    {
      title: 'Install the bridge',
      body: (
        <>
          Put <code className={codeChip}>ComfiConf-Bridge-0.1.0.jar</code> in your server’s{' '}
          <code className={codeChip}>plugins</code> folder and restart the server (Paper 26.1.2 or
          newer).
        </>
      ),
    },
    ...(bridge === HOSTED_BRIDGE_URL
      ? []
      : [
          {
            title: 'Point it at ComfiConf',
            body: (
              <>
                In <code className={codeChip}>plugins/ComfiConf/config.yml</code>, add{' '}
                <code className={cn(codeChip, 'break-all')}>backend-url: {bridge}</code> and{' '}
                <code className={cn(codeChip, 'break-all')}>web-url: {window.location.origin}</code>
                , then run <code className={codeChip}>/comficonf reconnect</code>.
              </>
            ),
          },
        ]),
    {
      title: 'Ask for a pairing code',
      body: (
        <>
          Run <code className={codeChip}>/comficonf pair</code> in the console or in game as an
          operator. The code is shown only to you and stays valid for 10 minutes.
        </>
      ),
    },
    {
      title: 'Enter the code in ComfiConf',
      body: (
        <>
          Type the 8-character code under Pair a server. Your server appears in your server list
          right away.
        </>
      ),
    },
  ];
  return (
    <ol className={cn(panelClasses, 'divide-y divide-border-soft')}>
      {steps.map((step, index) => (
        <li
          key={step.title}
          className={cn(
            'grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3 px-4',
            compact ? 'py-2.5' : 'py-3',
          )}
        >
          <span aria-hidden className="pt-px font-mono text-xs text-subtle">
            {String(index + 1).padStart(2, '0')}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium">{step.title}</p>
            <p className="mt-0.5 text-sm text-muted">{step.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function DashboardPage() {
  useDocumentTitle('Servers');
  const now = useNow();
  const session = useSession();
  const servers = useQuery(serversQuery());
  const loadError = useLoadError(servers);
  const list = servers.data ?? [];
  const onlineCount = list.filter((server) => server.status === 'online').length;
  const empty = servers.data !== undefined && list.length === 0;

  return (
    <PageContainer>
      <PageHeader
        title="Your servers"
        description={
          servers.data && list.length > 0
            ? `${pluralize(list.length, 'server')}, ${onlineCount} online. Choose one to edit its plugin settings.`
            : session.data
              ? `Signed in as ${session.data.user.email}.`
              : undefined
        }
        actions={
          <Button asChild variant={empty ? 'secondary' : 'primary'}>
            <Link to="/pair">
              <Plus aria-hidden /> Pair a server
            </Link>
          </Button>
        }
      />

      {loadError ? (
        <LoadFailedNotice
          title="Your servers could not be loaded"
          error={loadError}
          errorKey={servers.errorUpdatedAt}
          retrying={servers.isFetching}
          onRetry={() => void servers.refetch()}
        />
      ) : servers.isPending ? (
        <ServerSkeleton />
      ) : (
        <ServerListArea
          list={list}
          now={now}
          refreshError={servers.isError ? servers.error : null}
          refreshErrorAt={servers.errorUpdatedAt}
          refreshing={servers.isFetching}
          onRetry={() => void servers.refetch()}
        />
      )}
    </PageContainer>
  );
}

function ServerListArea({
  list,
  now,
  refreshError,
  refreshErrorAt,
  refreshing,
  onRetry,
}: {
  list: ServerSummary[];
  now: number;
  /** Error of the last refresh while `list` is still shown from an earlier load. */
  refreshError: Error | null;
  refreshErrorAt: number;
  /** Whether the list is being fetched again. */
  refreshing: boolean;
  onRetry: () => void;
}) {
  return (
    <>
      {refreshError ? (
        <RefreshFailedNotice
          errorKey={refreshErrorAt}
          retrying={refreshing}
          className="mb-4"
          title="Your servers could not be refreshed"
          error={refreshError}
          onRetry={onRetry}
        />
      ) : null}
      {list.length === 0 ? (
        <PageSection
          title="Connect your first server"
          description="ComfiConf talks to your Minecraft server through a small bridge plugin. It connects outward, so you do not need to open any ports."
        >
          <OnboardingSteps />
          <Button asChild variant="primary" className="mt-4">
            <Link to="/pair">
              <KeyRound aria-hidden /> Enter a pairing code
            </Link>
          </Button>
        </PageSection>
      ) : (
        <ServerTable list={list} now={now} />
      )}
    </>
  );
}
